import { viewerHoldsContact } from "../../../hooks/contacts/contactBookRules";
import type { CrmContactRow, CrmMemberPermissions } from "../../../@types/webotServer";

/**
 * MAY THE VIEWER EDIT THIS ROW — one rule, one place.
 *
 * ── The defect this exists for ──────────────────────────────────────────────────
 *
 * The CRM list rendered BYTE-IDENTICALLY on both tabs. Every affordance a realtor has
 * over their own book — the status pill, the 🔥, click-to-edit on twelve cells, "קשר נכס",
 * "ערוך פרטים" — was equally live on "מאגר המשרד", where most rows are a COLLEAGUE'S
 * record. Reported 2026-08-25: "I am able to set the status, link a listing, edit contacts
 * that are not mine — can you forbid that?".
 *
 * It was not a cosmetic leak. Three of those channels reached a real document:
 *
 *   1. the status/🔥 write goes STRAIGHT to `dashboard-users-v2/{row.ownerUserId}/…` from
 *      the browser, and `firestore.rules` grants an org admin `write` over a teammate's
 *      whole subtree — so for an admin it landed;
 *   2. in-cell editing posts `view: "org"` DELIBERATELY for a foreign owner, and the
 *      server's write path checks org membership and nothing else — so it landed for any
 *      member who could see the row;
 *   3. "ערוך פרטים" / "קשר נכס" open the full dialog over the COLLEAGUE'S hydrated
 *      document but save through `AppContext.updateExistingContact`, which sends no owner
 *      — so the server resolved the target to the CALLER and wrote the colleague's edited
 *      values onto the caller's own copy of that phone. A silent wrong-document write,
 *      not merely an unauthorized one. Closing the affordance closes that too.
 *
 * ── Why OWNERSHIP and not a permission axis ─────────────────────────────────────
 *
 * `crmPermissions` has four axes — pool / browse / export / delete — and no `edit`.
 * Inventing one would be a policy decision for agency owners to make, in a settings screen,
 * with a migration for 96 existing orgs. "Whose record is this" needs none of that, is
 * what the user actually asked for, and is the rule a realtor can predict without being
 * told: you edit your own contacts.
 *
 * ── Why the MINE view short-circuits ────────────────────────────────────────────
 *
 * `view: "mine"` is scoped server-side to `visibleOwnerIds: [userId]`, so every row it
 * returns is the caller's by construction — the comparison can only ever be true. Taking
 * the short-circuit is not an optimisation: `viewerUserId` comes from `useAuth`, which
 * resolves a tick after the first render, and a comparison against `null` is FALSE. Without
 * it the whole of a realtor's own book would render read-only for a frame on every load,
 * which is a far worse bug than the one being fixed.
 *
 * ── What it does NOT gate ───────────────────────────────────────────────────────
 *
 * Only writes to the CONTACT DOCUMENT. Deliberately still offered on a colleague's row:
 * quick notes (they live in `user-notes` keyed to the VIEWER — a private note about a
 * person you can see is the viewer's own data), "פגישה חדשה" / "משימה חדשה" (the viewer's
 * own calendar), "שלח מסמך לחתימה" and "שלח נכסים מתאימים" (navigations, and agency work
 * a manager legitimately does). Selection stays live too — the bulk MESSAGE hand-off is
 * still offered on the agency tab; the three that touch contact documents are not. See
 * `CrmBulkBar`.
 */

/**
 * Owner ids are agent EMAIL ADDRESSES arriving from independently-generated places — a
 * row's `owner_user_id` column, the auth user, the holders endpoint — which need not agree
 * on case or trailing space. The server normalizes identically (`contactWriteRules.ts`),
 * so a mismatch here would mean the UI and the endpoint disagreeing about the same row.
 */
export const normalizeCrmOwnerId = (id: string | null | undefined): string => (id ?? "").trim().toLowerCase();

/** Two owner ids naming the same agent. Absent never matches absent — see below. */
export const sameCrmOwner = (a: string | null | undefined, b: string | null | undefined): boolean => {
  const left = normalizeCrmOwnerId(a);
  // ⚠️ AN ABSENT ID MATCHES NOTHING, including another absent id. The natural-looking
  // `!owner || equal(...)` reads a missing owner as "mine", which is exactly the inference
  // that made the side panel announce a colleague's contact as deleted the moment its row
  // fell out of the loaded page. Unknown is not own.
  return Boolean(left) && left === normalizeCrmOwnerId(b);
};

/**
 * The row is the viewer's OWN copy, and therefore theirs to edit.
 *
 * `isOrgView === false` short-circuits to `true` — see the header for why that is
 * load-bearing rather than a shortcut.
 */
export const crmRowIsEditable = (
  row: Pick<CrmContactRow, "ownerUserId">,
  viewerUserId: string | null | undefined,
  isOrgView: boolean
): boolean => (isOrgView ? sameCrmOwner(row.ownerUserId, viewerUserId) : true);

// ---------------------------------------------------------------------------
// The POOLED book — one record per person for the office, "my list" beside it
// ---------------------------------------------------------------------------

/**
 * What the viewer may do to ONE row of a pooled agency's book (the server's plan D4,
 * `office-pool-design.md` §4). Every rule here MIRRORS the server's `pooledBookRules.ts`
 * — the server decides for real on every request; this decides which affordances are
 * worth drawing, and it must never offer one the server will refuse.
 *
 *   held                   the viewer is on this person's holder list (`holderIds`)
 *   canEdit                held, or the agency's pool is `full` — "you may edit what you
 *                          can see unredacted" (`assertMayEditPooledContact`)
 *   canAddToMyList         an office record the viewer does NOT hold — "הוסף ללקוחות שלי"
 *   canRemoveFromMyList    a record the viewer holds — "הסר מהלקוחות שלי", which is what
 *                          a delete MEANS on a pooled book by default
 *   canDeleteOfficeRecord  the `delete` permission AND the full pool — "מחק מלקוחות המשרד",
 *                          for everyone, behind a confirm (`decidePooledDelete`)
 *
 * `busy` (the switch is running) withholds every write at once: the server refuses them
 * all with a retry message, and a button that fails on click is worse than one that is
 * absent with the reason painted beside the list.
 */
export type CrmPooledRowAccess = {
  held: boolean;
  canEdit: boolean;
  canAddToMyList: boolean;
  canRemoveFromMyList: boolean;
  canDeleteOfficeRecord: boolean;
};

export const NO_POOLED_WRITES: CrmPooledRowAccess = {
  held: false,
  canEdit: false,
  canAddToMyList: false,
  canRemoveFromMyList: false,
  canDeleteOfficeRecord: false
};

/**
 * What a row's menu and the panel get handed on a POOLED book: the affordances above and
 * the three actions behind them, owned by the workspace (which holds the write hook, the
 * list's patch channel and the toast). `null` on a personal book, so every row paints
 * exactly as it always did.
 */
export type CrmPooledRowActions = {
  access: CrmPooledRowAccess;
  /** The agency, for the document read a menu's edit dialog makes under the agency view. */
  orgId: string | null;
  /** The switch is running: the actions are withheld and this says why. */
  busy: boolean;
  onAddToMyList: () => void;
  onRemoveFromMyList: () => void;
  /** Opens the workspace's confirm — the delete itself runs there. */
  onDeleteOfficeRecord: () => void;
};

export const crmPooledRowAccess = (
  row: Pick<CrmContactRow, "ownerUserId" | "bookKey" | "holderIds">,
  viewerUserId: string | null | undefined,
  permissions: CrmMemberPermissions | null | undefined,
  busy = false
): CrmPooledRowAccess => {
  const held = viewerHoldsContact(row, viewerUserId);
  if (busy) return { ...NO_POOLED_WRITES, held };
  const fullPool = permissions?.pool === "full";
  return {
    held,
    canEdit: held || fullPool,
    canAddToMyList: !held,
    canRemoveFromMyList: held,
    canDeleteOfficeRecord: Boolean(permissions?.delete) && fullPool
  };
};
